import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import type { Subscription } from 'rxjs';
import { of } from 'rxjs';
import { debounceTime, startWith, switchMap, tap } from 'rxjs/operators';

import { TranslatePipe } from '@recruitee/i18n';
import type { SelectOption } from '@recruitee/user-interface';
import {
  FormFieldComponent,
  InputComponent,
  LabelComponent,
  SelectComponent,
} from '@recruitee/user-interface';
import { uniqByKey } from '@recruitee/utils';

import { Destroyable, takeUntilDestroyed } from '../../../common';
import { COUNTRIES, REGIONS } from '../constants';
import { GfeaApiService } from '../gfea-api';
import type { Address } from '../types';

const STREET_NAME_MAX_LENGTH = 30;
@Component({
  selector: 'rti-gfea-address-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gfea-address-input.component.html',
  styleUrls: ['./gfea-address-input.component.less'],
  imports: [
    FormFieldComponent,
    InputComponent,
    LabelComponent,
    ReactiveFormsModule,
    SelectComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class GfeaAddressInputComponent {
  @Input() public group: UntypedFormGroup;

  public STREET_NAME_MAX_LENGTH = STREET_NAME_MAX_LENGTH;

  public countryCodeSelectOptions: SelectOption[] = [];
  public regionSelectOptions: SelectOption[] = [];
  public citySelectOptions: SelectOption[] = [];
  public addressesLoading: boolean = false;

  private postalCodeChangesSubscription: Subscription;

  constructor(
    private cdRef: ChangeDetectorRef,
    private gfeaApi: GfeaApiService,
  ) {}

  public ngOnInit(): void {
    this.countryCodeSelectOptions = COUNTRIES.map(country => ({
      value: country.code,
      label: country.name,
    }));

    if (this.group.value.countryCode) {
      this.initSelectOptionsForCountryCode(this.group.value.countryCode);
    }

    this.group.controls.countryCode.valueChanges.pipe(takeUntilDestroyed(this)).subscribe(value => {
      this.group.patchValue({
        postalCode: null,
        region: null,
        muncipality: null,
        streetName: null,
      });
      this.initSelectOptionsForCountryCode(value);
    });
  }

  public addressValidationEnabled(): boolean {
    return (
      !!this.group.get('postalCode') &&
      !!this.group.get('municipality') &&
      this.group.get('countryCode')?.value === 'DE'
    );
  }

  // eslint-disable-next-line max-lines-per-function
  private initSelectOptionsForCountryCode(countryCode: string): void {
    if (this.postalCodeChangesSubscription) this.postalCodeChangesSubscription.unsubscribe();

    if (this.addressValidationEnabled()) {
      this.regionSelectOptions = [];
      this.citySelectOptions = [];
      this.postalCodeChangesSubscription = this.group.controls.postalCode.valueChanges
        .pipe(
          startWith(this.group.controls.postalCode.value),
          takeUntilDestroyed(this),
          tap(() => {
            this.addressesLoading = true;
            this.cdRef.detectChanges();
          }),
          debounceTime(200),
          switchMap((postalCode: string) => {
            if (!postalCode) return of({ addresses: [] });

            return this.gfeaApi.post(`/addresses/search`, { postalCode });
          }),
        )
        .subscribe(({ addresses }: { addresses: Address[] }) => {
          this.regionSelectOptions = uniqByKey(
            addresses.reduce(
              (acc, address) => {
                const region = REGIONS.find(r => r.name === address.region);
                if (region) acc.push({ value: region.vamCode, label: region.name });
                return acc;
              },
              <SelectOption[]>[],
            ),
            'value',
          );
          if (!this.regionSelectOptions.find(option => option.value === this.group.value.region))
            this.group.controls.region.setValue(null);
          if (
            this.regionSelectOptions.length === 1 &&
            this.group.controls.region.value !== this.regionSelectOptions[0].value
          )
            this.group.controls.region.setValue(this.regionSelectOptions[0].value);

          this.citySelectOptions = uniqByKey(
            addresses.map(address => ({ value: address.city, label: address.city })),
            'value',
          );
          if (
            !this.citySelectOptions.find(option => option.value === this.group.value.municipality)
          )
            this.group.controls.municipality.setValue(null);
          if (
            this.citySelectOptions.length === 1 &&
            this.group.controls.municipality.value !== this.citySelectOptions[0].value
          )
            this.group.controls.municipality.setValue(this.citySelectOptions[0].value);

          this.addressesLoading = false;
          this.cdRef.detectChanges();
        });
    } else {
      this.regionSelectOptions = this.regionSelectOptionsForCountry(countryCode);
      this.citySelectOptions = [];
      this.addressesLoading = false;
    }

    this.cdRef.detectChanges();
  }

  private regionSelectOptionsForCountry(countryCode: string): SelectOption[] {
    return REGIONS.filter(region => region.countryCode === countryCode).map(region => ({
      value: region.vamCode,
      label: region.name,
    }));
  }
}
